import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import RestoreIcon from '@mui/icons-material/Restore';

import OnEditIcon from '../../../../icons/OnEditIcon';
import {
    clearBusinessContactCardAuditData,
    loadBusinessContactCardAuditData
} from '../../../../store/recruitmentService';
import { hasRole } from '../../../../utils/userRoles';
import AuditBusinessContactCardModal from '../../../auditing/auditBusinessContactCard/AuditBusinessContactCardModal';

import card from '../../../../commonStyles/card.module.css';

const _NAME_CHAR_LENGTH = 30;

const BusinessContactCard = ({
    roles,
    acceptedRoles,
    entry,
    onEdit,
    editable = false,
    isAuditable = false
}) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [isDisabled, setIsDisabled] = useState(false);
    const [showAuditModal, setShowAuditModal] = useState(false);
    const [auditLabel, setAuditLabel] = useState('');

    // USEEFFECTS
    useEffect(() => {
        editable && setIsDisabled(!hasRole(acceptedRoles, roles));
    }, [editable]);

    // EVENT HANDLERS
    const onSearchAuditItems = (cardId, field, label) => {
        if (cardId) dispatch(loadBusinessContactCardAuditData(cardId, field));
        setShowAuditModal(true);
        setAuditLabel(label ?? field);
    };

    const onAuditModalClose = (e, reason) => {
        e.stopPropagation();
        if (reason === 'backdropClick') return;
        setShowAuditModal(false);
        setAuditLabel('');
        dispatch(clearBusinessContactCardAuditData());
    };

    return (
        <div data-testid="business_contact_card" className={card.card}>
            <div
                className={`${
                    entry?.firstName?.length > _NAME_CHAR_LENGTH ||
                    entry?.lastName?.length > _NAME_CHAR_LENGTH
                        ? card.bannerDoubleRow
                        : card.banner
                } ${isDisabled ? card.disabledBanner : ''}`}>
                {editable && (
                    <span className={card.editIcon} data-testid="business_contact_card_edit_icon">
                        <OnEditIcon
                            onEdit={() => onEdit(entry)}
                            roles={roles}
                            acceptedRoles={acceptedRoles}
                            active={!isDisabled}
                        />
                    </span>
                )}
                {!isAuditable ? (
                    <h6>
                        {entry?.firstName && entry?.lastName
                            ? `Contact ${entry.firstName} ${entry.lastName}`
                            : 'Contact'}
                    </h6>
                ) : (
                    <h6>
                        {entry?.firstName && entry?.lastName ? (
                            <span className={card.bannerContentWithAudit}>
                                <span className={card.bannerName}>
                                    Contact {entry.firstName}
                                    <RestoreIcon
                                        onClick={() =>
                                            onSearchAuditItems(
                                                entry.id,
                                                'First Name',
                                                'Contact First Name'
                                            )
                                        }
                                    />
                                </span>
                                <span className={card.bannerName}>
                                    {entry.lastName}
                                    <RestoreIcon
                                        onClick={() =>
                                            onSearchAuditItems(
                                                entry.id,
                                                'Last Name',
                                                'Contact Last Name'
                                            )
                                        }
                                    />
                                </span>
                            </span>
                        ) : (
                            'Contact'
                        )}
                    </h6>
                )}
            </div>
            <div className={card.content} data-testid="card_content">
                <div>
                    <div className={card.subHeader}>Contact Notes:</div>
                    <div className={card.description}>
                        {entry?.notes?.length > 100
                            ? `${entry?.notes.substring(0, 97)}...`
                            : entry?.notes}
                    </div>
                </div>
                <div className={card.rows}>
                    <div className={card.row}>
                        <AccountCircleIcon />
                        {` ${entry?.positions ? entry?.positions : ' - '}`}
                    </div>
                    <div className={card.row}>
                        <PhoneIcon />
                        <span className={card.auditRow}>
                            {` ${entry?.officeNumber ? entry?.officeNumber : ' - '}`}
                            {isAuditable && (
                                <RestoreIcon
                                    onClick={() => onSearchAuditItems(entry.id, 'Office Number', 'Contact Office Number')}
                                />
                            )}
                        </span>
                    </div>
                    <div className={card.row}>
                        <PhoneIphoneIcon />
                        <span className={card.auditRow}>
                            {` ${entry?.mobileNumber ? entry?.mobileNumber : ' - '}`}
                            {isAuditable && (
                                <RestoreIcon
                                    onClick={() => onSearchAuditItems(entry.id, 'Mobile Number', 'Contact Mobile Number')}
                                />
                            )}
                        </span>
                    </div>
                    <div className={card.row}>
                        <EmailIcon />
                        <span className={card.auditRow}>
                            {` ${entry?.email ? entry?.email : ' - '}`}
                            {isAuditable && (
                                <RestoreIcon
                                    onClick={() => onSearchAuditItems(entry.id, 'Email', 'Contact Email Address')}
                                />
                            )}
                        </span>
                    </div>
                </div>
            </div>
            {showAuditModal && (
                <div id="auditBusinessContactCardModal">
                    <AuditBusinessContactCardModal
                        isOpen={showAuditModal}
                        label={auditLabel}
                        onClose={onAuditModalClose}
                    />
                </div>
            )}
        </div>
    );
};

BusinessContactCard.propTypes = {
    entry: PropTypes.object,
    roles: PropTypes.arrayOf(PropTypes.string),
    acceptedRoles: PropTypes.arrayOf(PropTypes.string),
    onEdit: PropTypes.func,
    editable: PropTypes.bool,
    isAuditable: PropTypes.bool
};

export default BusinessContactCard;
